import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { UserStateService } from '../services/user-state.service';
import { LoggingService } from '../services/logging.service';
import { map, catchError, of, switchMap } from 'rxjs';

/**
 * Onboarding Guard
 *
 * Simplified guard that only checks if onboarding is complete.
 * If incomplete, redirects to /resolving page which handles
 * determining the next step in the onboarding flow.
 *
 * This prevents multiple guard redirects and route flashing.
 */
export const onboardingGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const userStateService = inject(UserStateService);
  const logger = inject(LoggingService);

  return auth.isAuthenticated$.pipe(
    switchMap((isAuthenticated) => {
      if (!isAuthenticated) {
        // If not authenticated, redirect to Auth0 login with current URL preserved
        logger.info('[OnboardingGuard] User not authenticated, redirecting to Auth0 login with target:', state.url);
        auth.loginWithRedirect({
          appState: { target: state.url }
        });
        return of(false);
      }

      // Check if onboarding is complete using state
      return userStateService.getOrFetchState().pipe(
        map((status) => {
          // Check if ALL onboarding steps are complete using computed signal
          if (!userStateService.isOnboardingComplete()) {
            // Redirect to resolving page to determine next step, preserving target URL
            router.navigate(['/resolving'], { state: { target: state.url } });
            return false;
          }

          // Onboarding complete, allow access
          return true;
        }),
        catchError((error) => {
          logger.error('[OnboardingGuard] Error checking onboarding:', error);
          // Redirect to resolving page to handle the error, preserving target URL
          router.navigate(['/resolving'], { state: { target: state.url } });
          return of(false);
        })
      );
    })
  );
};
